import { useEffect, useRef, useState } from "react";
import { Eraser, Check } from "lucide-react";

/**
 * SignaturePad — canvas-based signature capture.
 * onChange(dataUrl | null) fires whenever the user finishes a stroke.
 */
export default function SignaturePad({ value, onChange, height = 160, testid = "signature-pad" }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [empty, setEmpty] = useState(!value);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Adjust for DPR for crisp lines
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = height * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#F1F5F9";
    ctx.fillStyle = "transparent";
    // If a prior value exists, restore it
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, height);
      img.src = value;
      setEmpty(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
  };

  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const end = () => {
    if (!drawing) return;
    setDrawing(false);
    setEmpty(false);
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onChange?.(dataUrl);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setEmpty(true);
    onChange?.(null);
  };

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="label-caps">Assinatura do Técnico</span>
        <div className="flex gap-2 items-center">
          {!empty && <span className="text-xs" style={{ color: "var(--success)" }}><Check size={12} className="inline mr-1" />Capturada</span>}
          <button type="button" className="btn btn-ghost text-xs" onClick={clear} data-testid={`${testid}-clear`}>
            <Eraser size={13} /> Limpar
          </button>
        </div>
      </div>
      <div className="border" style={{ borderColor: "var(--border-strong)", background: "#081321" }}>
        <canvas
          ref={canvasRef}
          style={{ width: "100%", height: `${height}px`, touchAction: "none", cursor: "crosshair", display: "block" }}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          data-testid={`${testid}-canvas`}
        />
      </div>
      <div className="text-xs mt-1" style={{ color: "var(--text-mute)" }}>
        Assine acima com o dedo (mobile) ou mouse para confirmar a retirada.
      </div>
    </div>
  );
}
